"use client";

import { cn } from "@erp/shared-libs/utils";
import { AnimatePresence, motion } from "motion/react";
import { CSSProperties, FC, ReactNode, useEffect, useRef, useState } from "react";

interface LazyRevealProps {
  children: ReactNode;
  once?: boolean;
  threshold?: number;
  rootMargin?: string;
  className?: string;
  style?: CSSProperties;
  /** Minimum height to reserve before children are mounted, e.g. "200px" or 200 */
  minHeight?: number | string;
  /** Whether to animate fade-in/out. Defaults to true. */
  animate?: boolean;
}

export const LazyReveal: FC<LazyRevealProps> = ({
  children,
  once = true,
  threshold = 0,
  rootMargin = "0px",
  className,
  style,
  minHeight,
  animate,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMounted(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setMounted(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once, threshold, rootMargin]);

  return (
    <div
      ref={wrapperRef}
      className={cn(className)}
      style={{ minHeight: mounted ? undefined : minHeight, ...style }}
    >
      {animate === true ? (
        <AnimatePresence>
          {mounted && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        mounted && children
      )}
    </div>
  );
};
